import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { User, Lock, Eye, EyeOff, Copy, Check, ChevronRight, X } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import MedicalAnimation from '../../components/MedicalAnimation';
import HexMedicalAnimation from '../../components/HexMedicalAnimation';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedCredential, setCopiedCredential] = useState('');
  const [showCredentials, setShowCredentials] = useState(false);
  const [animationVariant, setAnimationVariant] = useState('particles');
  const [isIdle, setIsIdle] = useState(false);
  const [demoUnlocked, setDemoUnlocked] = useState(false);
  const demoKeySequenceRef = React.useRef({ count: 0, timer: null });
  const { signIn, signInWithGoogle, user, userProfile, loading: authLoading, profileLoading, getRoleDashboardPath } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionReason = searchParams.get('reason');

  // Demo credentials for easy testing
  const demoCredentials = [
    { role: 'Admin', email: 'admin@kmchospital.com', password: 'm8Qx2pL9w', color: 'bg-red-500' },
    { role: 'Doctor', email: 'doctor@kmchospital.com', password: 'k7Nv4hR3t', color: 'bg-blue-500' },
    { role: 'Nurse', email: 'nurse@kmchospital.com', password: 'j6Bm1xT8q', color: 'bg-green-500' },
    { role: 'Lab Tech', email: 'labtech@kmchospital.com', password: 'r5Hc9wP2n', color: 'bg-purple-500' },
    { role: 'Pharmacist', email: 'pharmacist@kmchospital.com', password: 's3Yf7zK4m', color: 'bg-orange-500' },
    { role: 'Accountant', email: 'accountant@kmchospital.com', password: 'kobokobo11', color: 'bg-teal-500' }
  ];

  // Redirect if already authenticated
  useEffect(() => {
    if (user && userProfile && !authLoading && !profileLoading) {
      const dashboardPath = getRoleDashboardPath(userProfile?.role);
      navigate(dashboardPath);
    }
  }, [user, userProfile, authLoading, profileLoading, navigate, getRoleDashboardPath]);

  // Alt+9,9 key combo to unlock demo credentials panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === '9') {
        e.preventDefault();
        const seq = demoKeySequenceRef.current;
        seq.count += 1;
        if (seq.timer) clearTimeout(seq.timer);
        if (seq.count >= 2) {
          setDemoUnlocked(prev => !prev);
          setShowCredentials(false);
          seq.count = 0;
        } else {
          seq.timer = setTimeout(() => { seq.count = 0; }, 2000);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-hide demo toggle & panel after 5 seconds of inactivity
  const demoActivityRef = React.useRef(null);

  const resetDemoTimer = React.useCallback(() => {
    if (demoActivityRef.current) clearTimeout(demoActivityRef.current);
    demoActivityRef.current = setTimeout(() => {
      setShowCredentials(false);
      setDemoUnlocked(false);
    }, 5000);
  }, []);

  useEffect(() => {
    if (demoUnlocked) {
      resetDemoTimer();
      return () => {
        if (demoActivityRef.current) clearTimeout(demoActivityRef.current);
      };
    }
  }, [demoUnlocked, showCredentials, resetDemoTimer]);

  // Auto-switch animation variant every 20 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationVariant(prev => prev === 'particles' ? 'hex' : 'particles');
    }, 20000); // 20 seconds

    return () => clearInterval(interval);
  }, []);

  // Idle detection - hide login form after 15 seconds of inactivity
  useEffect(() => {
    let idleTimer;

    const resetIdleTimer = () => {
      setIsIdle(false);
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        setIsIdle(true);
      }, 15000); // 15 seconds
    };

    // Start the timer initially
    resetIdleTimer();

    // Add event listeners for user activity
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, resetIdleTimer);
    });

    return () => {
      clearTimeout(idleTimer);
      events.forEach(event => {
        window.removeEventListener(event, resetIdleTimer);
      });
    };
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: signInError } = await signIn(email, password);

      if (signInError) {
        if (signInError?.message?.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else if (signInError?.message?.includes('Failed to fetch') ||
          signInError?.message?.includes('AuthRetryableFetchError')) {
          setError('Cannot connect to authentication service. Please check your authentication provider (Firebase) and your network connection.');
        } else {
          setError(signInError?.message || 'Login failed. Please try again.');
        }
      }
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('NetworkError') ||
        error?.name === 'TypeError' && error?.message?.includes('fetch')) {
        setError('Cannot connect to authentication service. Please check your internet connection or contact support.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyCredential = async (credential) => {
    try {
      await navigator?.clipboard?.writeText(`${credential?.email} / ${credential?.password}`);
      setCopiedCredential(credential?.email);
      setTimeout(() => setCopiedCredential(''), 2000);
    } catch (error) {
      setEmail(credential?.email);
      setPassword(credential?.password);
    }
  };

  const fillCredential = (credential) => {
    setEmail(credential?.email);
    setPassword(credential?.password);
    setError('');
    setShowCredentials(false);
  };

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        {/* Background Animations with Crossfade */}
        <div
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: animationVariant === 'particles' ? 1 : 0 }}
        >
          <MedicalAnimation />
        </div>
        <div
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: animationVariant === 'hex' ? 1 : 0 }}
        >
          <HexMedicalAnimation />
        </div>
        <div className="relative z-10 min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Animations with Crossfade */}
      <div
        className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
        style={{ opacity: animationVariant === 'particles' ? 1 : 0 }}
      >
        <MedicalAnimation />
      </div>
      <div
        className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
        style={{ opacity: animationVariant === 'hex' ? 1 : 0 }}
      >
        <HexMedicalAnimation />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        {/* Compact Login Card - fades out when idle */}
        <div
          className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden transition-all duration-700 ease-in-out"
          style={{
            background: 'rgba(10, 20, 40, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 229, 255, 0.2)',
            boxShadow: '0 0 60px rgba(0, 229, 255, 0.15), 0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            opacity: isIdle ? 0 : 1,
            transform: isIdle ? 'scale(0.95) translateY(-20px)' : 'scale(1) translateY(0)',
            pointerEvents: isIdle ? 'none' : 'auto',
          }}
        >
          <div className="p-6">
            {/* Logo and Header */}
            <div className="text-center mb-4">
              <div className="flex justify-center mb-2">
                <img
                  src="/assets/images/KMCHospLogo-notext.png"
                  alt="KMC Hospital"
                  className="h-32 w-32 object-contain"
                  style={{
                    filter: 'drop-shadow(0 0 20px rgba(0, 229, 255, 0.4))',
                  }}
                />
              </div>
              <h1 className="text-xl font-bold text-white mb-0.5">KMC Hospital</h1>
              <p className="text-cyan-300/70 text-xs">Healthcare Management System</p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-cyan-200/80 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-4 w-4 text-cyan-400/60" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    className="block w-full pl-9 pr-3 py-2 border border-cyan-500/30 rounded-lg bg-slate-900/50 text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                    placeholder="Enter your email"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-cyan-200/80 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 text-cyan-400/60" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    className="block w-full pl-9 pr-9 py-2 border border-cyan-500/30 rounded-lg bg-slate-900/50 text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                    placeholder="Enter your password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-cyan-400/60 hover:text-cyan-300 transition-colors" />
                    ) : (
                      <Eye className="h-4 w-4 text-cyan-400/60 hover:text-cyan-300 transition-colors" />
                    )}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end -mt-1">
                <Link
                  to="/forgot-password"
                  className="text-xs text-cyan-400/70 hover:text-cyan-300 transition-colors font-medium"
                >
                  Forgot Password?
                </Link>
              </div>

              {/* Session Expiration / Suspension Notice */}
              {sessionReason && (
                <div className={`${sessionReason === 'suspended' ? 'bg-red-500/20 border-red-500/40 text-red-200' : 'bg-amber-500/15 border-amber-500/40 text-amber-200'} border rounded-lg p-3 flex items-start gap-2.5`}>
                  <Lock className={`w-4 h-4 ${sessionReason === 'suspended' ? 'text-red-400' : 'text-amber-400'} flex-shrink-0 mt-0.5`} />
                  <p className="text-xs leading-relaxed">
                    {sessionReason === 'suspended'
                      ? 'Your account has been suspended by the administrator. All active sessions were terminated.'
                      : sessionReason === 'status_revoked'
                      ? 'Your account status has changed and access has been revoked.'
                      : sessionReason === 'lock_timeout' || sessionReason === 'idle_timeout'
                      ? 'Workstation session expired due to inactivity. Patient records were protected. Please sign in to resume.'
                      : sessionReason === 'switch_user'
                      ? 'Workstation released. Please sign in with your credentials.'
                      : 'You have been signed out. Please sign in again.'}
                  </p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-2">
                  <p className="text-xs text-red-300">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-semibold text-slate-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                style={{
                  background: 'linear-gradient(135deg, #00e5ff 0%, #00bcd4 100%)',
                  boxShadow: loading ? 'none' : '0 0 20px rgba(0, 229, 255, 0.4)',
                }}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900 mr-2"></div>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
              
              <div className="relative mt-4 mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-cyan-500/30"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-slate-900 text-cyan-300/60">Or continue with</span>
                </div>
              </div>

              <button
                type="button"
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  const { error: googleError } = await signInWithGoogle();
                  if (googleError) {
                    setError(googleError.message);
                  }
                  setLoading(false);
                }}
                disabled={loading}
                className="w-full flex justify-center items-center py-2.5 px-4 border border-cyan-500/30 rounded-lg text-sm font-medium text-white bg-slate-800/50 hover:bg-slate-700/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Google
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Demo Credentials Toggle Button - Hidden behind Alt+9,9 key combo */}
      {demoUnlocked && (
        <button
          onClick={() => setShowCredentials(true)}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-20 flex items-center gap-2 py-3 px-3 rounded-l-lg text-sm font-medium text-white transition-all hover:pr-4"
          style={{
            background: 'rgba(0, 229, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 229, 255, 0.3)',
            borderRight: 'none',
            animation: 'fadeIn 0.3s ease-in-out',
          }}
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          <span className="hidden sm:inline">Demo</span>
        </button>
      )}

      {/* Slide-out Credentials Panel - Only in DOM when unlocked via Alt+9,9 */}
      {demoUnlocked && (
        <>
          <div
            onMouseMove={resetDemoTimer}
            onClick={resetDemoTimer}
            className={`fixed inset-y-0 right-0 z-30 w-80 max-w-full transform transition-transform duration-300 ease-in-out ${showCredentials ? 'translate-x-0' : 'translate-x-full'
              }`}
            style={{
              background: 'rgba(10, 20, 40, 0.95)',
              backdropFilter: 'blur(20px)',
              borderLeft: '1px solid rgba(0, 229, 255, 0.2)',
              boxShadow: showCredentials ? '-10px 0 40px rgba(0, 0, 0, 0.5)' : 'none',
            }}
          >
            <div className="h-full flex flex-col p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white">Demo Credentials</h2>
                <button
                  onClick={() => setShowCredentials(false)}
                  className="p-2 rounded-lg text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-cyan-300/60 text-sm mb-6">
                Use these test accounts to explore different role-based dashboards:
              </p>

              {/* Credentials List */}
              <div className="flex-1 overflow-y-auto space-y-3">
                {demoCredentials?.map((credential, index) => (
                  <div
                    key={index}
                    className="rounded-lg p-4 transition-all hover:bg-cyan-500/10"
                    style={{
                      background: 'rgba(0, 229, 255, 0.05)',
                      border: '1px solid rgba(0, 229, 255, 0.15)',
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className={`w-2.5 h-2.5 ${credential?.color} rounded-full mr-2`}></div>
                        <span className="font-semibold text-white text-sm">{credential?.role}</span>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => copyCredential(credential)}
                          className="p-1.5 hover:bg-cyan-500/20 rounded transition-colors"
                          title="Copy credentials"
                        >
                          {copiedCredential === credential?.email ? (
                            <Check className="h-4 w-4 text-green-400" />
                          ) : (
                            <Copy className="h-4 w-4 text-cyan-400" />
                          )}
                        </button>
                        <button
                          onClick={() => fillCredential(credential)}
                          className="px-3 py-1 rounded text-xs font-medium transition-colors text-slate-900"
                          style={{
                            background: 'linear-gradient(135deg, #00e5ff 0%, #00bcd4 100%)',
                          }}
                        >
                          Use
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-cyan-300/60 space-y-0.5">
                      <div>{credential?.email}</div>
                      <div className="font-mono text-cyan-300/80">{credential?.password}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer Info */}
              <div
                className="mt-4 p-4 rounded-lg"
                style={{
                  background: 'rgba(0, 229, 255, 0.05)',
                  border: '1px solid rgba(0, 229, 255, 0.1)',
                }}
              >
                <h3 className="font-semibold text-white text-sm mb-2">Role-Based Access</h3>
                <ul className="text-xs text-cyan-300/60 space-y-1">
                  <li>• <strong className="text-cyan-300/80">Admin:</strong> User management</li>
                  <li>• <strong className="text-cyan-300/80">Doctor:</strong> Patient consultations</li>
                  <li>• <strong className="text-cyan-300/80">Nurse:</strong> Patient care</li>
                  <li>• <strong className="text-cyan-300/80">Lab Tech:</strong> Laboratory tests</li>
                  <li>• <strong className="text-cyan-300/80">Pharmacist:</strong> Prescriptions</li>
                  <li>• <strong className="text-cyan-300/80">Accountant:</strong> Finance & billing</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Overlay when credentials panel is open */}
          {showCredentials && (
            <div
              className="fixed inset-0 z-20 bg-black/50"
              onClick={() => setShowCredentials(false)}
            />
          )}
        </>
      )}

      {/* Animation Toggle Button */}
      <button
        onClick={() => setAnimationVariant(prev => prev === 'particles' ? 'hex' : 'particles')}
        className="fixed right-4 bottom-4 z-20 px-4 py-2 rounded-full text-xs font-medium text-white transition-all hover:scale-105 active:scale-95"
        style={{
          background: 'rgba(10, 20, 40, 0.6)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 229, 255, 0.2)',
          boxShadow: '0 0 15px rgba(0, 229, 255, 0.1)',
        }}
      >
        Switch BG ({animationVariant === 'particles' ? 'Particles' : 'Hex Grid'})
      </button>
    </div>
  );
};

export default Login;